import { useState } from 'react'

import { FvButton, type IconName } from '@fv/client-components'

import { Pinline } from '@/components/Pinline'

import type { DayPanelShipment } from './types'

type Props = {
  shipments: DayPanelShipment[]
  currentItem: DayPanelShipment | null
  onSelect: (item: DayPanelShipment) => void
  onMove: (inc: number) => void
}

export const DayShipmentSelector = ({
  shipments,
  currentItem,
  onSelect,
  onMove,
}: Props) => {
  const [isShipmentListOpen, setShipmentListOpen] = useState(false)

  const hasShipments = shipments.length > 0
  const hasMultipleShipments = shipments.length > 1

  const selectableShipments = currentItem
    ? shipments.filter(s => s.loadId !== currentItem.loadId)
    : shipments

  return (
    <div className="flex items-center gap-x-2 mb-4">
      <NavBtn
        icon="chevron-left"
        disabled={!hasShipments}
        onClick={() => onMove(-1)}
      />
      <span className="text-sm italic">Previous</span>
      <Pinline dashed className="flex-1" />
      <div className="relative group">
        <FvButton
          className="font-medium flex items-center max-w-52 min-w-0 overflow-hidden"
          iconClass="text-fv-red -mr-1"
          buttonTextClass="block min-w-0 max-w-full truncate"
          onClick={() => {
            if (!hasMultipleShipments) {
              return
            }
            setShipmentListOpen(prev => !prev)
          }}
        >
          {currentItem ? currentItem.bolNumber : 'No shipments'}
        </FvButton>
        <div className="absolute h-[2px] left-0 right-0 -bottom-1 bg-fv-blue group-hover:bg-black"></div>

        {hasMultipleShipments && isShipmentListOpen && (
          <ShipmentDropdown
            shipments={selectableShipments}
            onSelect={item => {
              onSelect(item)
              setShipmentListOpen(false)
            }}
            onClose={() => setShipmentListOpen(false)}
          />
        )}
      </div>

      <Pinline dashed className="flex-1" />
      <span className="text-sm italic">Next</span>
      <NavBtn
        icon="chevron-right"
        disabled={!hasShipments}
        onClick={() => onMove(1)}
      />
    </div>
  )
}

type BtnProps = {
  disabled: boolean
  onClick: () => void
  icon: IconName
}
const NavBtn = ({ disabled, onClick, icon }: BtnProps) => (
  <FvButton
    icon={icon}
    className="rounded-full w-7 h-7 border-[.15rem] bg-white border-fv-blue-200"
    disabled={disabled}
    onClick={onClick}
  />
)

type ShipmentDropdownProps = {
  shipments: DayPanelShipment[]
  onSelect: (item: DayPanelShipment) => void
  onClose: () => void
}

const ShipmentDropdown = ({
  shipments,
  onSelect,
  onClose,
}: ShipmentDropdownProps) => {
  return (
    <>
      <div className="fixed inset-0 z-[9]" onMouseDown={onClose} />
      <div className="absolute left-1/2 top-4 z-10 mt-3 w-48 -translate-x-1/2 rounded-xs border border-fv-gray bg-white p-3 text-sm shadow-sm before:absolute before:left-1/2 before:top-0 before:h-2 before:w-2 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:bg-white before:border before:border-fv-gray before:border-b-0 before:border-r-0 max-h-[300px] overflow-auto">
        {shipments.length > 0 ? (
          <div className="flex flex-col gap-y-1">
            {shipments.map(item => (
              <div key={item.loadId}>
                <FvButton
                  theme="default"
                  className="w-full !h-auto !py-1 min-w-0"
                  buttonTextClass="truncate"
                  onClick={() => onSelect(item)}
                >
                  {item.bolNumber}
                </FvButton>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-fv-gray">No shipments</div>
        )}
      </div>
    </>
  )
}
